import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { divinationLabel } from '@/data/divination';
import { reqNo } from '@/data/format';
import NotificationBell from '@/components/NotificationBell';
import { birthDateText, birthTimeText } from '@/data/birth';
import { symbolOf } from '@/data/money';
import { questionGrade } from '@/data/scoring';
import AccuracyMeter from '@/components/AccuracyMeter';

const OUTCOME = {
  won: { text: '已成交', cls: 'bg-[#C93B2B] text-white' },
  pending: { text: '等待求測者選擇', cls: 'bg-[#F5F0E6] text-[#C93B2B]' },
  lost: { text: '未獲選', cls: 'bg-[#E5E0D8] text-[#5C5C5C]' },
  expired: { text: '已過期', cls: 'bg-[#E5E0D8] text-[#9A9A9A]' },
};

const AnsweredRequests = () => {
  const [answered, setAnswered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcomeFilter, setOutcomeFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/astrologer/answered-requests`, { withCredentials: true });
        setAnswered(res.data || []);
      } catch (error) {
        console.error('Error fetching answered requests:', error);
        toast.error('無法載入已回答求測單');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-[#E5E0D8] px-6 md:px-12 lg:px-24 py-4 flex justify-between items-center">
        <Link to="/astrologer/dashboard">
          <h2 className="text-xl font-serif text-[#C93B2B]">命理配對平台</h2>
        </Link>
        <NotificationBell />
      </nav>

      <div className="px-6 md:px-12 lg:px-24 py-12">
        <Link to="/astrologer/dashboard">
          <Button variant="ghost" className="mb-6 rounded-sm" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            返回
          </Button>
        </Link>

        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-serif text-[#1A1A1A] mb-2">
            已回答求測單
          </h1>
          <p className="text-base text-[#5C5C5C] mb-6">
            你回答過的求測單與結果
          </p>

          {!loading && answered.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-6" data-testid="outcome-tabs">
              {[['all', '全部'], ['pending', OUTCOME.pending.text], ['won', OUTCOME.won.text], ['lost', OUTCOME.lost.text], ['expired', OUTCOME.expired.text]].map(([k, label]) => {
                const n = k === 'all' ? answered.length : answered.filter((r) => (r.outcome || 'pending') === k).length;
                if (k !== 'all' && n === 0) return null;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setOutcomeFilter(k)}
                    className={`px-3 py-1.5 rounded-sm border text-sm transition-colors ${
                      outcomeFilter === k
                        ? 'bg-[#C93B2B] text-white border-[#C93B2B]'
                        : 'bg-white text-[#5C5C5C] border-[#E5E0D8] hover:bg-[#F5F0E6]'
                    }`}
                    data-testid={`outcome-tab-${k}`}
                  >
                    {label} <span className="opacity-75">{n}</span>
                  </button>
                );
              })}
            </div>
          )}

          {loading ? (
            <div className="text-center py-12">
              <p className="text-[#5C5C5C]">載入中...</p>
            </div>
          ) : answered.length === 0 ? (
            <div className="bg-white border border-[#E5E0D8] rounded-sm p-12 text-center">
              <p className="text-sm text-[#5C5C5C]">你尚未回答任何求測單</p>
            </div>
          ) : (
            <div className="space-y-6">
              {answered.filter((r) => outcomeFilter === 'all' || (r.outcome || 'pending') === outcomeFilter).map((req) => {
                const badge = OUTCOME[req.outcome] || OUTCOME.pending;
                return (
                  <div
                    key={req.request_id}
                    className="bg-white border border-[#E5E0D8] rounded-sm p-6 md:p-8"
                    data-testid={`answered-${req.request_id}`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex items-center gap-2">
                        <div className="inline-flex items-center border border-[#C93B2B] text-[#C93B2B] px-2 py-1 text-xs rounded-none font-serif tracking-widest">
                          {divinationLabel(req)}
                        </div>
                        {req.seq && <span className="text-xs text-[#9C9C9C] font-mono">{reqNo(req.seq)}</span>}
                      </div>
                      <span className={`inline-flex items-center px-2 py-1 text-xs rounded-sm ${badge.cls}`} data-testid={`outcome-${req.request_id}`}>
                        {badge.text}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div>
                        <p className="text-xs text-[#5C5C5C] mb-1">出生日期</p>
                        <p className="text-sm font-medium text-[#1A1A1A]">{birthDateText(req)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#5C5C5C] mb-1">出生時間</p>
                        <p className="text-sm font-medium text-[#1A1A1A]">{birthTimeText(req)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#5C5C5C] mb-1">性別</p>
                        <p className="text-sm font-medium text-[#1A1A1A]">{req.gender === 'male' ? '男' : '女'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-[#5C5C5C] mb-1">地區</p>
                        <p className="text-sm font-medium text-[#1A1A1A]">{req.region}</p>
                      </div>
                    </div>

                    <div className="bg-[#F5F0E6] rounded-sm p-4 space-y-1.5">
                      <p className="text-xs text-[#5C5C5C]">我的報價（逐範圍）</p>
                      {(req.my_quotes && req.my_quotes.length
                        ? req.my_quotes
                        : [{ scope: '諮詢', price: req.my_price ?? 0, consultation_time_minutes: req.my_consultation_minutes }]
                      ).map((q) => (
                        <div key={q.scope} className="flex justify-between text-sm">
                          <span className="text-[#5C5C5C]">{q.scope} · {q.consultation_time_minutes} 分鐘</span>
                          <span className="text-[#C93B2B] font-medium">{symbolOf(req.currency) || 'NT$'} {(q.price ?? 0).toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-sm border-t border-[#E5E0D8] pt-1">
                        <span className="text-[#1A1A1A] font-medium">合計</span>
                        <span className="text-[#C93B2B] font-medium">{symbolOf(req.currency) || 'NT$'} {(req.my_price ?? 0).toLocaleString()}</span>
                      </div>
                      <p className="text-xs text-[#5C5C5C] pt-1">回答時間：{new Date(req.answered_at).toLocaleDateString('zh-TW')}</p>
                    </div>

                    {req.score_visible && req.answer_score != null && (
                      <div className="mt-4">
                        <AccuracyMeter answer={{ answer_score: req.answer_score }} />
                      </div>
                    )}
                    {!req.score_visible && (
                      <p className="text-xs text-[#9A8A6A] mt-4" data-testid={`score-pending-${req.request_id}`}>
                        🔒 回答評分會喺求測單結束（求測者選定／完成／過期）後先公開。
                      </p>
                    )}

                    <div className="mt-4 space-y-3">
                      {(req.verification_questions || []).map((q) => {
                        const a = (req.my_answers || []).find((x) => x.question_order === q.order);
                        const g = req.score_visible ? questionGrade((req.question_scores || []).find((x) => x.order === q.order)) : null;
                        return (
                          <div key={`q-${req.request_id}-${q.order}`} className="border-l-2 border-[#C93B2B] pl-4">
                            <div className="flex items-center gap-2">
                              <p className="text-xs text-[#C93B2B] font-bold">問題 {q.order}</p>
                              {g && (
                                <span className={`inline-flex items-center border px-1.5 py-0.5 text-[10px] rounded-sm ${g.cls}`}>
                                  {g.label}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-[#5C5C5C]">{q.question_text}</p>
                            <p className="text-sm text-[#1A1A1A] mt-1">你的回答：{a ? a.answer_text : '—'}</p>
                          </div>
                        );
                      })}
                      {req.my_observation && (
                        <div className="bg-[#F5F0E6] rounded-sm p-3">
                          <p className="text-xs text-[#5C5C5C] font-bold mb-1">補充觀察</p>
                          <p className="text-sm text-[#1A1A1A]">{req.my_observation}</p>
                        </div>
                      )}
                    </div>

                    {req.outcome === 'won' && (
                      <p className="text-xs text-[#5C5C5C] mt-3">求測者已選擇你，請到工作台的「求測者諮詢室」聯絡求測者。</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AnsweredRequests;
